import SinglePageExporterVertical from './SinglePageExporterVertical.js';

/**
 * @module Scheduler/feature/export/verticalexporter/SinglePageUnscaledExporterVertical
 */

/**
 * A single page exporter for vertical mode. Used by the {@link Scheduler.feature.export.PdfExport} feature to export to
 * single page. Content is exported with dimensions required to fit all requested rows and columns. This allows
 * generating PDF page not constrained by the standard paper formats.
 *
 * You do not need to use this class directly.
 *
 * ### Extending exporter
 *
 * ```javascript
 * class MySinglePageExporter extends SinglePageUnscaledExporterVertical {
 *     // type is required for exporter
 *     static type = 'mysinglepageunscaledexporter';
 *
 *     get stylesheets() {
 *         const stylesheets = super.stylesheets;
 *
 *         stylesheets.forEach(styleNodeOrLinkTag => doSmth(styleNodeOrLinkTag))
 *
 *         return stylesheets;
 *     }
 * }
 *
 * const scheduler = new Scheduler({
 *     mode     : 'vertical',
 *     features : {
 *         pdfExport : {
 *             // this export feature is configured with only one exporter
 *             exporters : [MySinglePageExporter]
 *         }
 *     }
 * });
 *
 * // run export with the new exporter
 * scheduler.features.pdfExport.export({ exporter : 'mysinglepageunscaledexporter' });
 * ```
 *
 * @classtype verticalsinglepageunscaled
 * @extends Scheduler/feature/export/verticalexporter/SinglePageExporterVertical
 */
export default class SinglePageUnscaledExporterVertical extends SinglePageExporterVertical {
    static $name = 'SinglePageUnscaledExporterVertical';

    static type = 'verticalsinglepageunscaled';

    static get title() {
        // In case locale is missing exporter is still distinguishable
        return this.localize('L{SinglePageUnscaledExporterVertical.verticalsinglepageunscaled}');
    }
}

// HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.
SinglePageUnscaledExporterVertical.prototype.pagesExtractor = async function * pagesExtractor(config) {
    // When we prepared grid we stretched it horizontally, now we need to gather all rows
    // There are two ways:
    // 1. set component height to scrollable.scrollHeight value to render all rows at once (maybe a bit more complex
    // if rows have variable height)
    // 2. iterate over rows, scrolling new portion into view once in a while
    // #1 sounds simpler, but that might require too much rendering, let's scroll rows instead

    const
        me             = this,
        { client }     = config,
        {
            totalWidth,
            verticalTimeAxisMargin
        } = me.exportMeta,
        styles         = me.stylesheets;

    let totalHeight, header, footer;

    totalHeight = await me.renderPage(config);

    // Measure header and footer height
    if (config.headerTpl) {
        header = me.prepareHTML(config.headerTpl({ totalWidth }));
        const height = me.measureElement(header, 'b-export-header');
        totalHeight += height;
    }

    if (config.footerTpl) {
        footer = me.prepareHTML(config.footerTpl({ totalWidth }));
        const height = me.measureElement(footer, 'b-export-footer');
        totalHeight += height;
    }

    const html = me.buildPageHtml(config);

    // Now add style to stretch grid vertically
    styles.push(
        `<style>
                body, #${client.id} {
                    height: ${totalHeight}px !important;
                    width: ${totalWidth}px !important;
                }
                
                .b-page-wrap {
                    height: 100%;
                    direction: ${client.rtl ? 'rtl' : 'ltr'};
                }
                
                .b-verticaltimeaxiscolumn {
                    margin-top: ${verticalTimeAxisMargin}px;
                }
            </style>`
    );

    // This is a single page exporter, so we only yield one page
    yield {
        html : me.pageTpl({
            html,
            header,
            footer,
            styles,
            currentPage : 0
        })
    };
};
